import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import { useMercureEvents } from '@/hooks/useMercureEvents';
import { usePlaySound } from '@/hooks/usePlaySound';
import { useNativeOrderNotification } from '@/hooks/useNativeOrderNotification';
import { addLiveOrder } from '@/store/orders';
import {
  dismissIncomingCallToast,
  enqueueCallToast,
  enqueueOrderToast
} from '@/store/notifications';
import { resolveCall } from '@/store/calls';
import { markUnviewed } from '@/store/unviewedOrders';
import { CallEventsEnum } from '@/types/CallEventsEnum';
import { CallStatusesEnum } from '@/types/CallStatusesEnum';
import { type ICallEvent } from '@/types/ICallEvent';
import { type ICallHistoryEvent } from '@/types/ICallHistoryEvent';
import { type ISocketOrderEvent } from '@/types/ISocketOrderEvent';
import { buildCallNotificationId } from '@/utils/buildCallNotificationId';
import { toCallPayload } from '@/utils/toCallPayload';

/**
 * Единый глобальный слушатель Mercure: одно соединение на все топики
 * приложения (заказы и звонки). Живёт на уровне App (вне Routes), чтобы
 * соединение было одно на сессию и не рвалось при навигации. Подписка активна
 * только после проверки пользователя и при наличии токена. Сам «тупой»: вся
 * логика — в хуках, утилитах и сторе.
 */
export const MercureEventsListener: React.FC = () => {
  const dispatch = useAppDispatch();
  const isUserChecked = useAppSelector((state) => state.auth.isUserChecked);
  // tokensReady — реактивный признак записанных токенов (вместо нереактивного
  // AuthService.isAuthorized), чтобы подписка стартовала сразу после логина.
  const tokensReady = useAppSelector((state) => state.auth.tokensReady);
  const resolvedCalls = useAppSelector((state) => state.calls.resolvedCalls);
  const enabled = isUserChecked && tokensReady;

  const { play } = usePlaySound();
  const notifyNative = useNativeOrderNotification();

  const handleOrderEvent = useCallback(
    (event: ISocketOrderEvent) => {
      // Только тост: историю центра уведомлений больше не наполняем.
      dispatch(enqueueOrderToast(event));
      dispatch(markUnviewed(event.data.uuid));
      play();

      // Нативный ОС-баннер — только когда вкладка не видна (в фокусе свой тост);
      // вся условная логика (права/тумблер/видимость) внутри хука.
      notifyNative(event);

      // Эфемерный realtime-слой поверх таблицы: заказ встаёт на свою позицию
      // по порядковому номеру (хвост `formated_id`) на любой странице/фильтрах,
      // сбрасывается при запросе списка.
      dispatch(addLiveOrder(event.data));
    },
    [dispatch, play, notifyNative]
  );

  const handleCallEvent = useCallback(
    (event: ICallEvent) => {
      const payload = toCallPayload(event, 'incoming');

      // Любое не-INCOMING событие означает, что звонок уже отработан: гасим
      // карточку и запоминаем номер — на случай, если INCOMING по нему придёт
      // с опозданием (сокеты приходят не по порядку).
      if (event.event !== CallEventsEnum.INCOMING) {
        dispatch(
          resolveCall({ phone: payload.phone, occurredAt: payload.occurredAt })
        );
        dispatch(
          dismissIncomingCallToast(buildCallNotificationId(payload.phone))
        );
        return;
      }

      const resolvedAt = resolvedCalls[payload.phone];

      if (resolvedAt !== undefined && resolvedAt >= payload.occurredAt) {
        return;
      }

      dispatch(enqueueCallToast(payload));
      play();
    },
    [dispatch, play, resolvedCalls]
  );

  const handleCallHistoryEvent = useCallback(
    (event: ICallHistoryEvent) => {
      const payload = toCallPayload(event, 'missed');

      // Любой исторический статус терминален — звонок закончился. Пополняем
      // реестр, чтобы опоздавший INCOMING не поднял карточку по завершённому
      // звонку (топики независимы, порядок доставки не гарантирован).
      dispatch(
        resolveCall({ phone: payload.phone, occurredAt: payload.occurredAt })
      );

      // Реестр здесь намеренно НЕ читаем: пропущенному почти всегда
      // предшествует живое терминальное событие, уже записавшее номер, — сверка
      // с реестром заблокировала бы сама себя.
      if (event.status === CallStatusesEnum.MISSED) {
        dispatch(enqueueCallToast(payload));
        return;
      }

      // Прочие статусы тоста не дают, но подтверждают, что звонок отработан:
      // гасим карточку входящего, даже если живое событие о ней не дошло.
      dispatch(
        dismissIncomingCallToast(buildCallNotificationId(payload.phone))
      );
    },
    [dispatch]
  );

  useMercureEvents({
    onOrderEvent: handleOrderEvent,
    onCallEvent: handleCallEvent,
    onCallHistoryEvent: handleCallHistoryEvent,
    enabled
  });

  return null;
};
